import { useEffect, useRef, useState } from 'react';
import { Dialog } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n';

/**
 * Take a photo with the device camera (webcam on a PC, front camera on a phone
 * when the native capture picker is not used). Streams into a <video>, snaps to
 * a canvas, and hands back a JPEG blob — the same thing a chosen file would be.
 */
export function CameraDialog({ open, onClose, onCapture }: { open: boolean; onClose: () => void; onCapture: (blob: Blob) => void }) {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shot, setShot] = useState<{ blob: Blob; url: string } | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setShot(null);
    setReady(false);
    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t('portrait.camera.unsupported'));
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } }, audio: false });
        if (cancelled) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream;
          await v.play().catch(() => undefined);
          setReady(true);
        }
      } catch {
        setError(t('portrait.camera.denied'));
      }
    };
    void start();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((tr) => tr.stop());
      streamRef.current = null;
    };
  }, [open, t]);

  useEffect(() => () => { if (shot) URL.revokeObjectURL(shot.url); }, [shot]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const side = Math.min(v.videoWidth, v.videoHeight);
    const c = document.createElement('canvas');
    c.width = side;
    c.height = side;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    // Mirror so the snapshot matches what the person saw in the preview.
    ctx.translate(side, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(v, (v.videoWidth - side) / 2, (v.videoHeight - side) / 2, side, side, 0, 0, side, side);
    c.toBlob((blob) => {
      if (blob) setShot({ blob, url: URL.createObjectURL(blob) });
    }, 'image/jpeg', 0.9);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('portrait.camera.title')}
      labelledBy="camera-title"
      actions={
        shot ? (
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setShot(null)}>
              <Icon name="refresh" />
              <span>{t('portrait.camera.retake')}</span>
            </button>
            <button type="button" className="btn btn-primary" onClick={() => onCapture(shot.blob)}>
              <Icon name="check" />
              <span>{t('portrait.camera.use')}</span>
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-primary btn-lg" onClick={snap} disabled={!ready || !!error}>
            <Icon name="camera" />
            <span>{t('portrait.camera.snap')}</span>
          </button>
        )
      }
    >
      <div className="camera-stage">
        {error ? (
          <p className="muted">{error}</p>
        ) : shot ? (
          <img className="camera-preview" src={shot.url} alt="" />
        ) : (
          <video ref={videoRef} className="camera-preview is-live" playsInline muted autoPlay />
        )}
      </div>
      {!error && !shot && <p className="small muted mt-2">{t('portrait.camera.help')}</p>}
    </Dialog>
  );
}
